"use client"

import { useState, useEffect } from "react"
import Image from "next/image"
import { RevealOnScroll } from "@/components/reveal-on-scroll"

// Helper function to find image field in event data
function getImageFromEvent(event: Event): string | null {
    // Check common image field names
    const imageFields = ['image', 'picture', 'img', 'photo', 'image_name', 'activity_image']

    for (const field of imageFields) {
        if (event[field] && typeof event[field] === 'string' && event[field].trim() !== '') {
            console.log(`🖼️ Found image in field '${field}':`, event[field])
            return event[field]
        }
    }

    console.log('❌ No image found in any field. Available fields:', Object.keys(event))
    console.log('🖼️ Event data:', event)
    return null
}

// Component to handle different image URL patterns
function EventImage({ imageName, alt, eventData }: { imageName: string, alt: string, eventData: any }) {
    const [currentUrlIndex, setCurrentUrlIndex] = useState(0)

    // Use the same URL pattern as gallery (which works)
    const possibleUrls = [
        `https://dextro-online.com/stjerome_school/web_admin/${imageName}`,
        `https://dextro-online.com/stjerome_school/API/uploads/${imageName}`,
        `https://dextro-online.com/stjerome_school/upload/${imageName}`,
        `https://dextro-online.com/stjerome_school/uploads/${imageName}`,
        imageName.startsWith('http') ? imageName : null, // In case it's already a full URL
    ].filter(Boolean) as string[]

    const handleImageError = () => {
        console.error('❌ Image failed to load:', possibleUrls[currentUrlIndex])
        console.log('🖼️ Event data:', eventData)
        console.log('🖼️ Image name from API:', imageName)

        if (currentUrlIndex < possibleUrls.length - 1) {
            console.log('🔄 Trying next URL pattern:', possibleUrls[currentUrlIndex + 1])
            setCurrentUrlIndex(currentUrlIndex + 1)
        } else {
            console.error('❌ All image URL patterns failed for:', imageName)
        }
    }

    const handleImageLoad = () => {
        console.log('✅ Image loaded successfully:', possibleUrls[currentUrlIndex])
    }

    if (currentUrlIndex >= possibleUrls.length) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
                <span className="text-sm">Image not found</span>
                <span className="text-xs mt-1 text-gray-400">{imageName}</span>
            </div>
        )
    }

    return (
        <Image
            src={possibleUrls[currentUrlIndex]}
            alt={alt}
            fill
            className="object-contain"
            sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
            onError={handleImageError}
            onLoad={handleImageLoad}
        />
    )
}


type Event = {
    id: number
    activity_heading: string
    activity_desc: string
    image?: string
    picture?: string
    img?: string
    photo?: string
    image_name?: string
    activity_image?: string
    [key: string]: any // Allow any additional fields
}

export function EventsSection() {
    const [events, setEvents] = useState<Event[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch('https://dextro-online.com/stjerome_school/API/get_latestevents.php?action=get_events')

                console.log('🎉 Fetching events from:', response.url)
                console.log('🎉 Response status:', response.status)

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                const data = await response.json()
                console.log('🎉 Events API Response:', data)

                if (data.success && Array.isArray(data.events)) {
                    console.log('✅ Events loaded:', data.events.length, 'events')
                    console.log('🖼️ Sample event data:', data.events[0])
                    console.log('🖼️ All event fields:', Object.keys(data.events[0] || {}))
                    console.log('🖼️ Image field value:', data.events[0]?.image)
                    setEvents(data.events)
                } else if (Array.isArray(data)) {
                    console.log('✅ Events loaded (direct array):', data.length, 'events')
                    console.log('🖼️ Sample event data:', data[0])
                    console.log('🖼️ All event fields:', Object.keys(data[0] || {}))
                    console.log('🖼️ Image field value:', data[0]?.image)
                    setEvents(data)
                } else {
                    console.error('❌ Events API Error:', data.error || 'Invalid response format')
                    setError(data.error || 'Invalid response format from API')
                }
            } catch (err) {
                console.error('❌ Error fetching events:', err)
                setError(`Failed to load events: ${err instanceof Error ? err.message : 'Unknown error'}`)
            } finally {
                setLoading(false)
            }
        }

        fetchEvents()
    }, [])



    if (loading) {
        return (
            <section className="py-16 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#113545] mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading events...</p>
                    </div>
                </div>
            </section>
        )
    }

    return (
        <section className="py-16 bg-gray-50">
            <div className="container mx-auto px-4">
                {/* Header */}
                <RevealOnScroll animationClass="animate-fade-in">
                    <div className="text-center mb-12">
                        <h1 className="font-serif text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            School Events
                        </h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Stay updated with all the exciting events, activities, and celebrations happening at our school.
                        </p>
                    </div>
                </RevealOnScroll>

                {error && (
                    <div className="text-center mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800">{error}</p>
                        <button
                            onClick={() => {
                                setLoading(true)
                                setError(null)
                                // Retry fetch
                                const fetchEvents = async () => {
                                    try {
                                        const response = await fetch('https://dextro-online.com/stjerome_school/API/get_latestevents.php?action=get_events')
                                        console.log('🔄 Retrying events API call')
                                        const data = await response.json()
                                        console.log('🔄 Retry response:', data)

                                        if (data.success && Array.isArray(data.events)) {
                                            setEvents(data.events)
                                            setError(null)
                                        } else if (Array.isArray(data)) {
                                            setEvents(data)
                                            setError(null)
                                        } else {
                                            setError(data.error || 'Invalid response format from API')
                                        }
                                    } catch (err) {
                                        console.error('❌ Retry Error:', err)
                                        setError(`Failed to load events: ${err instanceof Error ? err.message : 'Unknown error'}`)
                                    } finally {
                                        setLoading(false)
                                    }
                                }
                                fetchEvents()
                            }}
                            className="mt-4 px-6 py-2 bg-[#113545] text-white rounded-lg hover:bg-[#6b1515] transition-colors"
                        >
                            Retry Loading Events
                        </button>
                        <p className="text-sm text-red-600 mt-2">Check browser console for detailed error logs</p>
                    </div>
                )}

                {/* Events Grid */}
                {events.length > 0 ? (
                    <RevealOnScroll animationClass="animate-slide-in-up">
                        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                            {events.map((event) => (
                                <div
                                    key={event.id}
                                    className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
                                >
                                    {/* Event Image */}
                                    <div className="relative h-48 overflow-hidden bg-gray-200">
                                        {(() => {
                                            const imageField = getImageFromEvent(event)
                                            return imageField ? (
                                                <EventImage
                                                    imageName={imageField}
                                                    alt={event.activity_heading}
                                                    eventData={event}
                                                />
                                            ) : (
                                                <div className="flex flex-col items-center justify-center h-full text-gray-500 p-4">
                                                    <span className="text-sm">No Image Available</span>
                                                    <div className="text-xs mt-2 text-gray-400 text-center">
                                                        <div>Event ID: {event.id}</div>
                                                        <div className="mt-1">Available fields:</div>
                                                        <div className="text-xs">{Object.keys(event).join(', ')}</div>
                                                    </div>
                                                </div>
                                            )
                                        })()}
                                    </div>

                                    {/* Event Content */}
                                    <div className="p-6">
                                        <h3 className="font-serif text-xl font-bold text-gray-900 mb-4 line-clamp-2">
                                            {event.activity_heading}
                                        </h3>

                                        <p className="text-gray-600 line-clamp-4 leading-relaxed">
                                            {event.activity_desc}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </RevealOnScroll>
                ) : (
                    <div className="text-center py-16">
                        <div className="text-6xl mb-4">📅</div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">No Events Found</h3>
                        <p className="text-gray-600">
                            No events are currently scheduled. Please check back later for updates.
                        </p>
                    </div>
                )}
            </div>
        </section>
    )
}